import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiClientError,
  type ApiClientErrorCode,
  type PaintCategory,
  type PaintListItem,
  type PaintTone,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useCatalogFilters } from '@/features/handles-catalog/useCatalogFilters';
import { PaintsCatalogView, type PaintsCatalogState } from './PaintsCatalogView';
import type { PaintFilterField } from './PaintFilterChips';

/**
 * Container Phase 3 (Каталог красок). См. ADR-0005 + ADR-0010.
 *
 * Связывает URL-state, локальный preview-state чипов, fetch через apiClient →
 * передаёт данные Presenter'у.
 *
 * **Phase 3 интерпретирует ADR-0010 §2 «group-by-ralCode» как default filter
 * `categories=['Кухня']`** — при пустом URL автоматически применяется этот
 * фильтр, дедуплицируя 426 SKU до 213 уникальных RAL (один per ralCode).
 * Toggle на 'Шкаф' меняет видимое фото для тех же 213 RAL. Alt-toggle между
 * Кухней и Шкафом для одного цвета — Phase 4 детальная карточка (через
 * PhotoPicker UI).
 *
 * Cursor pagination: «Показать ещё» подгружает следующую страницу через
 * `apiClient.paint.list({ cursor })`. Items накапливаются (а не перезаписывают);
 * сброс при смене фильтров.
 *
 * Initial state: `success` (а не `idle` как в DecorsCatalog) — paint без
 * upstream KindPicker'а нуждается в faster path to value (см. ADR-0010 §2 +
 * code-review C0 valid'ация). `idle` достижим только при явном reset all.
 *
 * **Default filter применяется ровно один раз** — после reset all default
 * `categories=['Кухня']` НЕ переприменяется автоматически. Пользователь
 * увидит idle state с пустыми chip'ами и подсказкой «Выберите фильтры».
 * Это intentional UX: повторное появление chip'ов выглядело бы как «reset
 * не сработал». См. integration test «reset all → clears URL».
 */
const DEFAULT_LIMIT = 20;
const PAINT_FIELDS = ['tones', 'categories'] as const;
const DEFAULT_CATEGORIES: readonly PaintCategory[] = ['Кухня'];

export function PaintsCatalog() {
  const navigate = useNavigate();

  const { filters: appliedFilters, hasFilters, apply, reset } = useCatalogFilters(PAINT_FIELDS);
  const appliedTones = useMemo(
    () => (appliedFilters.tones ?? []) as readonly PaintTone[],
    [appliedFilters.tones],
  );
  const appliedCategories = useMemo(
    () => (appliedFilters.categories ?? []) as readonly PaintCategory[],
    [appliedFilters.categories],
  );

  // Default filter «Кухня» при пустом URL — Phase 3 интерпретация ADR-0010 §2.
  // Применяем единожды через **useRef-guard**, не useState: setState async, под
  // React StrictMode dev double-effect второй вызов effect'а всё ещё видит
  // прошлое значение state'а и применил бы default повторно (URL дублировался
  // как `?categories=Кухня&categories=Кухня`). useRef.current — синхронная
  // мутация, второй вызов effect'а сразу видит `true` и skip'ит.
  // (Возможный side-effect: уменьшение количества параллельных fetch'ей,
  // что **может** также устранить связанный MSW lifecycle TypeError —
  // требует подтверждения через browser smoke после fix-серии.)
  const defaultAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultAppliedRef.current) return;
    defaultAppliedRef.current = true;
    if (!hasFilters) {
      apply({ tones: [], categories: [...DEFAULT_CATEGORIES] });
    }
  }, [hasFilters, apply]);

  // Preview-state чипов до клика «Найти». Инициализируется applied-фильтрами
  // для поддержки deep-link.
  const [selectedTones, setSelectedTones] = useState<readonly PaintTone[]>(appliedTones);
  const [selectedCategories, setSelectedCategories] =
    useState<readonly PaintCategory[]>(appliedCategories);

  // Sync preview ← applied при асинхронном изменении applied (default-apply
  // useEffect завершился позже первого render'а; URL обновился извне через
  // history-back; deep-link rehydrate). Без этого sync'а chip'ы оставались
  // визуально пустыми (aria-pressed=false), хотя URL уже содержал фильтр.
  // Sync безопасен относительно user-flow: dep — сам appliedTones/Categories
  // (мемоизированный reference, стабилен между ре-рендерами без URL-change),
  // поэтому клик пользователя по chip'у (изменяет только preview) не
  // триггерит этот effect.
  useEffect(() => {
    setSelectedTones(appliedTones);
  }, [appliedTones]);
  useEffect(() => {
    setSelectedCategories(appliedCategories);
  }, [appliedCategories]);

  const [items, setItems] = useState<readonly PaintListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);

  // JSON-stringify дешевле чем reactive deps на массивах для useEffect MVP
  // (паттерн DecorsCatalog).
  const filtersKey = JSON.stringify(appliedFilters);

  useEffect(() => {
    if (!hasFilters) {
      setItems([]);
      setNextCursor(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setItems([]);
    setNextCursor(null);
    let cancelled = false;
    apiClient.paint
      .list({
        tones: [...appliedTones],
        categories: [...appliedCategories],
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          if (cancelled) return;
          setItems(res.items);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          if (cancelled) return;
          // Не leak-аем String(err) в UI (security audit, mirror DecorsCatalog:91-97).
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [hasFilters, filtersKey, appliedTones, appliedCategories]);

  const onLoadMore = () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    setError(null);
    apiClient.paint
      .list({
        tones: [...appliedTones],
        categories: [...appliedCategories],
        cursor: nextCursor,
        limit: DEFAULT_LIMIT,
      })
      .then(
        (res) => {
          setItems((prev) => [...prev, ...res.items]);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
          setLoading(false);
        },
      );
  };

  // 5 состояний (P-021). loading-флаг во время «Показать ещё» не должен
  // переключать success → loading; используем `items.length === 0` как guard.
  const state = useMemo<PaintsCatalogState>(() => {
    if (error) return 'error';
    if (loading && items.length === 0) return 'loading';
    if (!hasFilters && items.length === 0) return 'idle';
    if (items.length === 0) return 'empty';
    return 'success';
  }, [error, loading, items.length, hasFilters]);

  const onToggle = (field: PaintFilterField, value: string) => {
    const toggle = <T extends string>(prev: readonly T[], v: T): readonly T[] =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v];
    if (field === 'tones') setSelectedTones((prev) => toggle(prev, value as PaintTone));
    else setSelectedCategories((prev) => toggle(prev, value as PaintCategory));
  };

  const onResetGroup = (field: PaintFilterField) => {
    if (field === 'tones') setSelectedTones([]);
    else setSelectedCategories([]);
  };

  const onSearch = () =>
    apply({
      tones: [...selectedTones],
      categories: [...selectedCategories],
    });

  const onResetAll = () => {
    // Manual setState'ы дают синхронный preview-feedback (chip'ы погашаются
    // сразу). Sync-useEffect (через applied←URL) подтверждает их позже на
    // следующем render'е после `reset()`. Удалить эти setState'ы технически
    // безопасно, но добавит видимый flash «выбранные → погасли» от lag'а
    // URL-обновления.
    setSelectedTones([]);
    setSelectedCategories([]);
    reset();
  };

  return (
    <PaintsCatalogView
      state={state}
      items={items}
      nextCursor={nextCursor}
      error={error ?? undefined}
      selectedTones={selectedTones}
      selectedCategories={selectedCategories}
      onToggle={onToggle}
      onResetGroup={onResetGroup}
      onSearch={onSearch}
      onResetAll={onResetAll}
      onLoadMore={onLoadMore}
      onItemClick={(globalId) => navigate(`/paint/${encodeURIComponent(globalId)}`)}
    />
  );
}
